import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  LocalFireDepartment as FireIcon,
  Description as PermitIcon,
  Gavel as LawIcon,
  Shield as FpaIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Phone as PhoneIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

const FDI_TABLE = [
  { range: '0 – 20', level: 'Safe', rating: 'Insignificant', color: '#2196f3', colorName: 'Blue', description: 'Low fire risk. Normal activities may proceed.' },
  { range: '21 – 45', level: 'Moderate', rating: 'Low', color: '#4caf50', colorName: 'Green', description: 'Some fire risk. Exercise caution with open flames.' },
  { range: '46 – 60', level: 'Dangerous', rating: 'Medium', color: '#ffeb3b', colorName: 'Yellow', description: 'Significant fire risk. Permits may be restricted.' },
  { range: '61 – 75', level: 'Very Dangerous', rating: 'High', color: '#ff9800', colorName: 'Orange', description: 'High fire risk. Burning permits suspended.' },
  { range: '76+', level: 'Extremely Dangerous', rating: 'Extremely High', color: '#f44336', colorName: 'Red', description: 'Extreme fire risk. All burning prohibited.' },
];

const InfoPage = () => {
  const [expanded, setExpanded] = useState('fdi');

  const handleChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <>
      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 220, md: 300 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: 'url(/images/hero-forest-fire.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.7) 100%)',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: 3 }}>
          <InfoIcon sx={{ fontSize: 56, color: '#fff', mb: 1 }} />
          <Typography
            variant="h3"
            component="h1"
            fontWeight="bold"
            sx={{ color: '#fff', textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}
          >
            Fire Safety Information
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: 'rgba(255,255,255,0.85)', mt: 1, textShadow: '0 1px 6px rgba(0,0,0,0.5)' }}
          >
            Permits, legislation, and fire danger guidelines for South Africa
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 5 }}>
        {/* Quick reference cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%', borderTop: '4px solid #f44336' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <FireIcon sx={{ fontSize: 40, color: '#f44336', mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">Fire Danger Index</Typography>
                <Typography variant="body2" color="text.secondary">
                  How fire risk is measured and what each level means
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%', borderTop: '4px solid #ff9800' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <PermitIcon sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">Burning Permits</Typography>
                <Typography variant="body2" color="text.secondary">
                  When and how to obtain a permit for controlled burning
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%', borderTop: '4px solid #2196f3' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <FpaIcon sx={{ fontSize: 40, color: '#2196f3', mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">FPA Membership</Typography>
                <Typography variant="body2" color="text.secondary">
                  Your obligations and rights as an FPA member
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2} sx={{ height: '100%', borderTop: '4px solid #4caf50' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <LawIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">The Law</Typography>
                <Typography variant="body2" color="text.secondary">
                  Key legislation governing veld and forest fires
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Fire Danger Index ─────────────────────────────────────────── */}
        <Accordion expanded={expanded === 'fdi'} onChange={handleChange('fdi')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <FireIcon color="error" />
              <Typography variant="h6" fontWeight="bold">Fire Danger Index (FDI)</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              The <strong>Fire Danger Index (FDI)</strong> is the official measure of fire risk in South Africa,
              calculated using the <strong>DAFF Lowveld Model</strong> as prescribed by the National Veld and
              Forest Fire Act, 1998 (Act No. 101 of 1998). It takes into account temperature, humidity,
              wind speed, and recent rainfall to produce a single number indicating how dangerous conditions
              are for veld and forest fires.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
              How the FDI is Calculated
            </Typography>
            <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, mb: 3, backgroundColor: 'grey.50' }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                <strong>Step 1:</strong> Initial FDI = (T - 35) - ((35 - T) / 30) + ((100 - RH) × 0.37) + 30
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mb: 1.5 }}>
                Where T = temperature (°C) and RH = relative humidity (%)
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                <strong>Step 2:</strong> Wind Correction — add 0 to 40 points based on wind speed brackets
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2, mb: 1.5 }}>
                0-2 km/h = +0 | 3-8 = +5 | 9-16 = +10 | 17-25 = +15 | 26-32 = +20 | 33-36 = +25 | 37-41 = +30 | 42-45 = +35 | 46+ = +40
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                <strong>Step 3:</strong> Rain Correction — multiply by a factor (0.1 – 1.0) based on rainfall amount and days since rain
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                Heavy recent rain reduces the FDI significantly; as days pass the correction returns to 1.0
              </Typography>
            </Paper>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Danger Levels
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell><strong>FDI Range</strong></TableCell>
                    <TableCell><strong>Danger Level</strong></TableCell>
                    <TableCell><strong>Rating</strong></TableCell>
                    <TableCell><strong>Colour</strong></TableCell>
                    <TableCell><strong>Implications</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {FDI_TABLE.map((row) => (
                    <TableRow key={row.range}>
                      <TableCell><strong>{row.range}</strong></TableCell>
                      <TableCell>{row.level}</TableCell>
                      <TableCell>{row.rating}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.colorName}
                          size="small"
                          sx={{
                            backgroundColor: row.color,
                            color: row.colorName === 'Yellow' ? '#000' : '#fff',
                            fontWeight: 'bold',
                          }}
                        />
                      </TableCell>
                      <TableCell>{row.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningIcon sx={{ color: '#e65100' }} />
                <Typography variant="subtitle2" sx={{ color: '#e65100' }}>Important</Typography>
              </Box>
              <Typography variant="body2">
                The FDI is updated hourly. When the FDI exceeds 60 (Orange), all burning permits are
                automatically suspended regardless of any permit that may have been issued. Landowners
                must check the daily FDI before conducting any burning operations.
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* ── Burning Permits ──────────────────────────────────────────── */}
        <Accordion expanded={expanded === 'permits'} onChange={handleChange('permits')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PermitIcon sx={{ color: '#ff9800' }} />
              <Typography variant="h6" fontWeight="bold">Burning Permits & Procedures</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              Under the <strong>National Veld and Forest Fire Act (Act 101 of 1998)</strong>, any person
              who intends to burn firebreaks or conduct controlled burning must comply with specific
              requirements. Burning without meeting these requirements is an offence.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Requirements for Controlled Burning
            </Typography>
            <List dense>
              {[
                'Be a registered member of a Fire Protection Association (FPA) for the area',
                'Provide at least 14 days written notice to all adjacent landowners and the FPA',
                'Ensure sufficient firebreaks are in place around the burn area',
                'Have adequate personnel and equipment on standby to control the fire',
                'Burn only when the Fire Danger Index is below 45 (Blue or Green)',
                'Burn only between sunrise and sunset unless special permission is granted',
                'Designate a competent person to supervise the burn at all times',
                'Notify the local fire department and/or FPA before commencing the burn',
              ].map((item, i) => (
                <ListItem key={i}>
                  <ListItemIcon><CheckIcon sx={{ color: '#4caf50' }} /></ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              When Burning is Prohibited
            </Typography>
            <List dense>
              {[
                'When the FDI exceeds 60 (Yellow, Orange or Red) — all permits are automatically suspended',
                'During wind speeds exceeding 30 km/h',
                'At night, unless specifically authorised',
                'When you have not given proper notice to neighbours',
                'If you are not a member of the relevant FPA',
              ].map((item, i) => (
                <ListItem key={i}>
                  <ListItemIcon><WarningIcon sx={{ color: '#f44336' }} /></ListItemIcon>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>

            <Paper variant="outlined" sx={{ p: { xs: 1, sm: 2 }, mt: 2, backgroundColor: '#e3f2fd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PhoneIcon sx={{ color: '#1565c0' }} />
                <Typography variant="subtitle2" sx={{ color: '#1565c0' }}>How to Apply</Typography>
              </Box>
              <Typography variant="body2">
                Contact your local FPA office to apply for a burning permit. You will need to provide
                details of the area to be burned, the purpose of the burn, and your planned safety
                measures. The FPA will assess the application and may conduct a site inspection before
                issuing a permit. Allow at least 21 days for processing.
              </Typography>
            </Paper>
          </AccordionDetails>
        </Accordion>

        {/* ── FPA Information ──────────────────────────────────────────── */}
        <Accordion expanded={expanded === 'fpa'} onChange={handleChange('fpa')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <FpaIcon sx={{ color: '#2196f3' }} />
              <Typography variant="h6" fontWeight="bold">Fire Protection Associations (FPAs)</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              A <strong>Fire Protection Association (FPA)</strong> is a body established in terms of
              Chapter 2 of the National Veld and Forest Fire Act. FPAs are registered with the
              Department of Agriculture, Forestry and Fisheries (DAFF) and serve as the primary
              mechanism for coordinating veld fire prevention and management at a local level.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Purpose of an FPA
                </Typography>
                <List dense>
                  {[
                    'Coordinate the prevention and fighting of veld fires in the area',
                    'Facilitate the management of firebreaks by landowners',
                    'Share firefighting resources and equipment among members',
                    'Disseminate fire danger warnings and weather information',
                    'Provide training for fire prevention and firefighting',
                    'Liaise with municipalities and emergency services',
                    'Maintain records of fires and fire-related incidents',
                  ].map((item, i) => (
                    <ListItem key={i}>
                      <ListItemIcon><CheckIcon sx={{ color: '#2196f3', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Member Obligations
                </Typography>
                <List dense>
                  {[
                    'Prepare and maintain firebreaks on the boundary of your property',
                    'Have adequate equipment and personnel to fight fires',
                    'Comply with the rules and directives of the FPA',
                    'Assist neighbouring members in firefighting when called upon',
                    'Report all wildfires immediately to the FPA and emergency services',
                    'Attend FPA meetings and training sessions as required',
                    'Pay FPA membership fees as determined by the association',
                  ].map((item, i) => (
                    <ListItem key={i}>
                      <ListItemIcon><CheckIcon sx={{ color: '#ff9800', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, p: 2, backgroundColor: '#e8f5e9', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ color: '#2e7d32', mb: 0.5 }}>
                Why FPA Membership Matters
              </Typography>
              <Typography variant="body2">
                Under Section 34 of the Act, any landowner who is <strong>not</strong> a member of an FPA
                is presumed to have been negligent if a fire starts or spreads from their property. FPA
                membership provides a legal defence and demonstrates that you have taken reasonable steps
                to prevent and combat veld fires. It also gives you access to shared resources, training,
                and early warning systems.
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* ── Legislation ──────────────────────────────────────────────── */}
        <Accordion expanded={expanded === 'law'} onChange={handleChange('law')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LawIcon sx={{ color: '#4caf50' }} />
              <Typography variant="h6" fontWeight="bold">Legislation & Legal Framework</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body1" paragraph>
              Veld and forest fire management in South Africa is governed by several pieces of legislation.
              The primary Act is the <strong>National Veld and Forest Fire Act, 1998 (Act No. 101 of 1998)</strong>,
              which establishes the legal framework for fire prevention, firefighting, and liability.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Key Legislation
            </Typography>

            <Paper variant="outlined" sx={{ mb: 2 }}>
              <Box sx={{ p: { xs: 1, sm: 2 }, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  National Veld and Forest Fire Act, 1998 (Act 101 of 1998)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  The primary legislation for veld fire management. Establishes FPAs, regulates
                  firebreaks, sets out the duty to fight fires, and defines offences and penalties.
                </Typography>
              </Box>
              <Box sx={{ p: { xs: 1, sm: 2 }, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  National Forests Act, 1998 (Act 84 of 1998)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Provides additional protections for indigenous forests and plantations. Makes it
                  an offence to damage a forest by fire, with penalties including fines and imprisonment.
                </Typography>
              </Box>
              <Box sx={{ p: { xs: 1, sm: 2 }, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  National Environmental Management Act, 1998 (NEMA)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Imposes a general duty of care to prevent environmental degradation, including
                  damage caused by uncontrolled fires.
                </Typography>
              </Box>
              <Box sx={{ p: { xs: 1, sm: 2 } }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Disaster Management Act, 2002 (Act 57 of 2002)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Governs the response to fire disasters at national, provincial, and municipal levels.
                  Provides for the declaration of fire disasters and the coordination of emergency responses.
                </Typography>
              </Box>
            </Paper>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Offences & Penalties
            </Typography>
            <List dense>
              {[
                {
                  primary: 'Failure to prepare firebreaks',
                  secondary: 'Fine or imprisonment up to 2 years, or both (Section 25)',
                },
                {
                  primary: 'Negligent burning — fire escaping from your property',
                  secondary: 'Liable for damages caused; presumed negligent if not an FPA member (Section 34)',
                },
                {
                  primary: 'Burning without notice to neighbours',
                  secondary: 'Fine or imprisonment up to 2 years (Section 25)',
                },
                {
                  primary: 'Refusing to assist in fighting a fire when called upon',
                  secondary: 'Fine or imprisonment up to 1 year (Section 24)',
                },
                {
                  primary: 'Setting a fire during prohibited conditions (high FDI)',
                  secondary: 'Fine or imprisonment up to 3 years, or both (Section 25)',
                },
              ].map((item, i) => (
                <ListItem key={i} sx={{ py: 1 }}>
                  <ListItemIcon><LawIcon sx={{ color: '#795548', fontSize: 20 }} /></ListItemIcon>
                  <ListItemText
                    primary={item.primary}
                    secondary={item.secondary}
                    primaryTypographyProps={{ fontWeight: 'bold', variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>

            <Box sx={{ mt: 2, p: 2, backgroundColor: '#fce4ec', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WarningIcon sx={{ color: '#c62828' }} />
                <Typography variant="subtitle2" sx={{ color: '#c62828' }}>Legal Liability</Typography>
              </Box>
              <Typography variant="body2">
                Under Section 34, if a veld fire spreads from your land to another person's land and
                causes damage, you are <strong>presumed negligent</strong> unless you can prove that you
                are a member of an FPA, you had proper firebreaks, and you did not act negligently.
                This reversed burden of proof makes FPA membership critical for landowners.
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Footer attribution */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="text.disabled">
            This information is for general guidance only and does not constitute legal advice.
            Always consult the relevant legislation and your FPA for specific requirements.
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default InfoPage;
